// ADR-133: Reports → Ingestion Sync Runs — data hooks.
// Same infinite-loading shape as useDirectManagerDiscrepancies (ADR-010):
// items accumulate across loadMore(); a filter change resets and refetches.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ingestionSyncRunsApi } from '../services/api/ingestionSyncRunsApi';

const emptyPageInfo = { hasNextPage: false, endCursor: null, totalCount: 0 };

const usePagedList = (fetchPage, filters, enabled = true) => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState(emptyPageInfo);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const filterKey = JSON.stringify(filters);
  const pageInfoRef = useRef(emptyPageInfo);
  pageInfoRef.current = pageInfo;

  const load = useCallback(async (cursor, append = false) => {
    if (!enabled) {
      setItems([]);
      setPageInfo(emptyPageInfo);
      setIsLoading(false);
      return;
    }
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const result = await fetchPage({ ...filters, cursor });
      setItems((prev) => (append ? [...prev, ...(result?.items || [])] : (result?.items || [])));
      setPageInfo(result?.pageInfo || emptyPageInfo);
    } catch (err) {
      setError(err);
      if (!append) {
        setItems([]);
        setPageInfo(emptyPageInfo);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, enabled]);

  useEffect(() => {
    load(undefined, false);
  }, [load]);

  const loadMore = useCallback(() => {
    const pi = pageInfoRef.current;
    if (pi?.hasNextPage && pi.endCursor) {
      load(pi.endCursor, true);
    }
  }, [load]);

  const refetch = useCallback(() => load(undefined, false), [load]);

  return { items, pageInfo, isLoading, isLoadingMore, error, loadMore, refetch };
};

/** Runs list. Filters: { provider, syncType, status[], mode, batchId, limit }. */
export const useSyncRunsList = (filters = {}) => usePagedList(ingestionSyncRunsApi.listRuns, filters);

/** Run detail: { run, batch, sections }. */
export const useSyncRunDetail = (runId) => {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!runId) {
      setDetail(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setDetail(await ingestionSyncRunsApi.getRun(runId));
    } catch (err) {
      setError(err);
      setDetail(null);
    } finally {
      setIsLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return { detail, isLoading, error, refetch: fetchDetail };
};

/** Items of one section. Filters: { runId, section, employeeId, q, limit }. */
export const useSyncRunItems = (filters = {}) => {
  const { runId, ...rest } = filters;
  const fetchPage = useCallback((params) => {
    const query = { ...params };
    delete query.runId;
    return ingestionSyncRunsApi.listItems(runId, query);
  }, [runId]);
  return usePagedList(fetchPage, { runId, ...rest }, Boolean(runId && rest.section));
};
